import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { ibeStylistsCommissionsCreate, ibeStylistsCommissionsRead, ibeStylistsCommissionsUpdate } from 'actions/stylist/beStylistsCommissions.action';
import Permitted from 'components/core/Permitted.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import Tooltip from 'components/generic/Tooltip.component';
import TruncatedText from 'components/generic/TruncatedText.component';
import CommissionsToolbar from 'components/hr/CommissionsToolbar.component';
import PERM from 'constants/core/perm.const';
import DELAY from 'constants/delay.const';
import TRANSFER_POLICY_TEXT from 'constants/pos/stripeTransferPolicyText.const';
import useAsyncFnFix from 'hooks/useAsyncFnFix.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import { firstOf } from 'utils/arrays/elements.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import asInput from 'utils/strings/asInput.util';
import fixed from 'utils/values/fixed.util';

import CN from './StaffCommissions.module.scss';

const L = logger('StaffCommissions');

const MIN_VALUE = 0;
const MAX_VALUE = 100.0;

const EXTRA_CATEGORY = {
  allProducts: {
    categoryId: 'allProducts',
    categoryNameTrKey: 'humanResources.splitTakings.commissionTable.extraRolls.retailSales',
  },
  noShow: {
    categoryId: 'noShow',
    categoryNameTrKey: 'humanResources.splitTakings.commissionTable.extraRolls.noShowFees',
  },
};

const EXTRA_CATS = [EXTRA_CATEGORY.allProducts, EXTRA_CATEGORY.noShow];
const EXTRA_IDS = EXTRA_CATS.map(($) => $.categoryId);

const TRANSFER_POLICY_MAP = {
  [TRANSFER_POLICY_TEXT.allTransfers]: {
    labelTrKey: 'humanResources.splitTakings.commissionTable.headers.fullTakings',
    tooltipTrKey: 'humanResources.splitTakings.commissionTable.tooltips.allTransfers',
  },
  [TRANSFER_POLICY_TEXT.splitCommissions]: {
    labelTrKey: 'humanResources.splitTakings.commissionTable.headers.splitTakings',
    tooltipTrKey: 'humanResources.splitTakings.commissionTable.tooltips.splitCommissions',
  },
  [TRANSFER_POLICY_TEXT.tipOnly]: {
    labelTrKey: 'humanResources.splitTakings.commissionTable.headers.tipsOnly',
    tooltipTrKey: 'humanResources.splitTakings.commissionTable.tooltips.allTransfers',
  },
  [TRANSFER_POLICY_TEXT.noTransfer]: {
    labelTrKey: 'humanResources.splitTakings.commissionTable.headers.noAccount',
    tooltipTrKey: 'humanResources.splitTakings.commissionTable.tooltips.noTransfer',
  },
};

const getTransferPolicy = (transferPolicy, t) => {
  const policy = TRANSFER_POLICY_MAP[transferPolicy];

  if (!policy) {
    return {
      label: t('humanResources.splitTakings.commissionTable.unknownPolicy'),
      tooltip: t('humanResources.splitTakings.commissionTable.unknownPolicy'),
    };
  }

  return {
    label: t(policy.labelTrKey),
    tooltip: t(policy.tooltipTrKey),
  };
};

const fromStylistToColumn = (item) => ({
  stylistId: item?.stylistId,
  stylistName: item?.stylistName,
  transferPolicy: item?.transferPolicy,
});
const fromCategoryToRow = (item) => ({ categoryId: item?.categoryId, categoryName: item?.categoryName });

const byStylist = (object, item) => {
  const { commissions, stylistId, stylistName, transferPolicy } = item ?? {};
  const { id: commissionId, objectId: subCategoryId, objectType, percentage } = firstOf(commissions) ?? {};

  object[stylistId] = { stylistId, stylistName, transferPolicy, commissionId, subCategoryId, objectType, percentage };
  return object;
};

const byCategory = (object, item) => {
  const categoryId = item?.categoryId;
  object[categoryId] = asArray(item?.stylists)
    .map((stylist) => ({ ...stylist, categoryId }))
    .reduce(byStylist, {});
  return object;
};

const fromCommissionsToTable = (data) => {
  const cols = asArray(firstOf(data?.category)?.stylists).map(fromStylistToColumn);
  const rows = asArray(data?.category).map(fromCategoryToRow).concat(EXTRA_CATS);

  const allProducts = asArray(data?.allProducts).reduce(byStylist, {});
  const noShow = asArray(data?.noShow).reduce(byStylist, {});

  const categories = asArray(data?.category).reduce(byCategory, {});

  const matrix = { ...categories, allProducts, noShow };

  const result = { cols, rows, matrix };
  L.debug('fromCommissionsToTable()', result, '<-', data);
  return result;
};

const T = Object.freeze({
  clearMax: 'CLEAR_EXCEEDS_MAX',
  exceedsMax: 'VALUE_EXCEEDS_MAX',
  resetAll: 'RESET_ALL_STATE',
  resetOne: 'RESET_ONE_ITEM',
  setVal: 'SET_VALUE',
  updateFail: 'UPDATE_FAILED',
  updateInit: 'UPDATE_INITIALIZED',
});

const editStateReducer = (state, action) => {
  state = asObject(state);

  const type = fromAnyToTrimmed(action?.type);
  const key = fromAnyToTrimmed(action?.key);
  const item = state?.[key] ?? {};

  if (T.resetAll === type) return asObject();
  if (T.resetOne === type && state[key]) {
    const copy = { ...state };
    delete copy[key];
    return copy;
  }

  if (T.clearMax === type && item.exceedsMax) return { ...state, [key]: { ...item, exceedsMax: false } };
  if (T.exceedsMax === type) return { ...state, [key]: { ...item, exceedsMax: true, timeoutId: action?.timeoutId } };
  if (T.setVal === type) return { ...state, [key]: { ...item, value: action?.value } };
  if (T.updateFail === type) return { ...state, [key]: { updating: false, exceedsMax: false } };
  if (T.updateInit === type) return { ...state, [key]: { updating: true } };

  return state;
};

const isCommissionValid = (value) => Number.isFinite(value) && MIN_VALUE <= value && value <= MAX_VALUE;

const StaffCommissions = ({ ibeStylistsCommissionsRead, ibeStylistsCommissionsCreate, ibeStylistsCommissionsUpdate }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [editState, dispatch] = useReducer(editStateReducer, {}, void 1);

  const cancelTimeout = useCallback(
    (ev) => {
      const { timeoutId } = editState?.[ev?.key] ?? {};

      if (!timeoutId) return;
      clearTimeout(timeoutId);
    },
    [editState],
  );

  const handleCommissionChange = useCallback(
    (ev) => {
      L.debug('handleCommissionChange()', ev);
      const value = fromAnyToNumber(ev?.value);
      const key = ev?.key;

      // don't want prior timeouts to interfere any longer
      cancelTimeout({ key });

      if (value > MAX_VALUE) {
        // gets scheduled first, but the exceedsMax state will change first to true, then to false
        const timeoutId = delayed(DELAY.autohide, () => dispatch({ type: T.clearMax, key }));
        return void dispatch({ type: T.exceedsMax, key, timeoutId });
      }

      dispatch({ type: T.setVal, key, value });
    },
    [cancelTimeout],
  );

  const [commissionsReadResult, handleCommissionsRead] = useAsyncFnFix(async () => {
    const result = await ibeStylistsCommissionsRead();
    if (!result.is.ok) L.warn('handleCommissionsRead()', 'result:', result);
    return fromCommissionsToTable(result.json);
  }, [ibeStylistsCommissionsRead]);

  const [commissionsUpdateResult, handleCommissionsUpdate] = useAsyncFnFix(
    async (ev) => {
      const key = ev?.key;
      const value = editState?.[key]?.value;

      if (!isCommissionValid(value)) return void dispatch({ type: T.resetOne, key });

      L.debug('handleCommissionsUpdate()', 'IN', value, ev);

      dispatch({ type: T.updateInit, key });

      try {
        const { stylistId, categoryId } = ev ?? {};
        const { commissionId, subCategoryId, objectType } = ev?.cell ?? {};

        const data = { stylistId, objectType, objectId: subCategoryId, categoryId, percentage: fixed(value) };

        L.debug('handleCommissionsUpdate()', 'OUT', stylistId, commissionId, subCategoryId, data);
        if (null === commissionId) {
          await ibeStylistsCommissionsCreate({ data, stylistId });
        } else {
          await ibeStylistsCommissionsUpdate({ data, stylistId, commissionId });
        }

        await handleCommissionsRead();
        dispatch({ type: T.resetAll }); // reset all temporary state now that there is fresh data from BE

        setTooltipVisible((prev) => ({ ...(prev ?? void 1), [key]: true }));

        delayed(DELAY.autohide2, () => {
          setTooltipVisible((prev) => ({ ...(prev ?? void 1), [key]: false }));
        });
      } catch (e) {
        dispatch({ type: T.updateFail, key });
        throw e;
      }
    },
    [editState, handleCommissionsRead, ibeStylistsCommissionsCreate, ibeStylistsCommissionsUpdate],
  );

  const isAlreadyInitialized = commissionsReadResult?.value || commissionsReadResult.loading;
  useEffect(() => {
    if (isAlreadyInitialized) return;
    // noinspection JSIgnoredPromiseFromCall
    handleCommissionsRead();
  }, [handleCommissionsRead, isAlreadyInitialized]);

  const table = commissionsReadResult?.value;
  const isSpinnerRendered = !isAlreadyInitialized && (commissionsReadResult.loading || commissionsUpdateResult.loading);

  if (commissionsReadResult.error || commissionsUpdateResult.error) {
    L.warn('()', { commissionsReadResult, commissionsUpdateResult, table, editState });
  }

  return (
    <div
      data-bem="StaffCommissions"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <CommissionsToolbar />

      <Permitted not value={PERM.canEditStaffCommissions} tag="StaffCommissions__can-not-edit-commissions">
        <AlertPanel tag="AlertPanel" variant="warningPanel">
          {t('humanResources.splitTakings.warningPanelMsgPart1')}
          <br />
          {t('humanResources.splitTakings.warningPanelMsgPart2')}
        </AlertPanel>
      </Permitted>
      <Permitted value={PERM.canEditStaffCommissions} tag="StaffCommissions__can-edit-commissions">
        {isSpinnerRendered ? (
          <OverlaySpinner />
        ) : (
          <div className={CN.outer}>
            <div className={CN.tableContainer}>
              <div className={CN.tableHeader} data-bem="StaffCommissions__header">
                <div className={CN.headerFixedCell}>
                  {t('humanResources.splitTakings.commissionTable.headers.staffName')}
                  <br />
                  {t('humanResources.splitTakings.commissionTable.headers.payoutStatus')}
                </div>
                {asArray(table?.cols).map((col) => {
                  const { label, tooltip } = getTransferPolicy(col.transferPolicy, t);

                  return (
                    <div key={col.stylistId} className={CN.cellHeader}>
                      <TruncatedText max={32} suffix="..." value={col.stylistName} />
                      <Tooltip variant="slickBottomToBottom" width="s2" content={<div className={CN.policyTooltipText}>{tooltip}</div>}>
                        <div className={CN.badgeOuter}>
                          <Badge height="md" variant={col.transferPolicy === TRANSFER_POLICY_TEXT.splitCommissions ? 'success' : 'restricted'}>
                            <div className={CN.bargeContainer}>
                              <Icon variant={col.transferPolicy === TRANSFER_POLICY_TEXT.splitCommissions ? 'tick' : 'xRed'} width="xs" />
                              <div
                                className={cn({
                                  [CN.policyText]: true,
                                  [CN.confirm]: col.transferPolicy === TRANSFER_POLICY_TEXT.splitCommissions,
                                })}
                              >
                                {label}
                              </div>
                            </div>
                          </Badge>
                        </div>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
              <div className={CN.scrollBody}>
                {asArray(table?.rows).map((row) => {
                  const categoryId = String(row?.categoryId);
                  const isFirstRow = table?.rows?.[0]?.categoryId === row?.categoryId;
                  const isExtraRow = EXTRA_IDS.includes(categoryId);

                  return (
                    <div data-bem="StaffCommisions__row" data-c-id={categoryId} key={categoryId} className={CN.tableRow}>
                      <div className={CN.fixedColumnCell}>
                        {isExtraRow ? (
                          <Badge variant="fine" name={t(row?.categoryNameTrKey)} />
                        ) : (
                          <Badge variant="sky">
                            <div className={CN.categoryText}>
                              <TruncatedText max={32} suffix="..." value={row?.categoryName} />
                            </div>
                          </Badge>
                        )}
                      </div>

                      {asArray(table?.cols).map((col) => {
                        const stylistId = String(col?.stylistId);
                        const cell = table?.matrix?.[categoryId]?.[stylistId] ?? {};
                        const key = `c:${categoryId}-s:${stylistId}`;

                        const isTooltipVisible = tooltipVisible?.[key];
                        const inputValue = asInput(editState?.[key]?.value ?? (cell.commissionId ? fixed(cell.percentage) : ''));

                        L.debug('()', 'tooltip', { key, inputValue, isTooltipVisible });

                        return (
                          <div
                            className={CN.cell}
                            data-bem="StaffCommisions__cell"
                            data-c-id={categoryId}
                            data-s-sid={stylistId}
                            data-k={key}
                            data-v={inputValue}
                            data-tooltip={String(isTooltipVisible)}
                            key={key}
                          >
                            {isMissing(cell.percentage) ? (
                              '-'
                            ) : (
                              <div className={CN.inputContainer}>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={editState?.[key]?.updating ? '' : inputValue}
                                  onChange={(ev) => void handleCommissionChange({ key, value: fromEventToValue(ev) })}
                                  onBlur={() => void handleCommissionsUpdate({ key, stylistId, categoryId, cell })}
                                  className={cn({
                                    [CN.input]: true,
                                    [CN.error]: editState?.[key]?.exceedsMax,
                                  })}
                                />

                                {isTooltipVisible && (
                                  <div
                                    className={cn({
                                      [CN.updatingTooltip]: true,
                                      [CN.bottomPosition]: isFirstRow,
                                    })}
                                  >
                                    <div>
                                      {inputValue}
                                      {t('humanResources.splitTakings.commissionTable.tooltips.percentageToStaff')}
                                    </div>
                                    <div>
                                      {fixed(100 - inputValue)}
                                      {t('humanResources.splitTakings.commissionTable.tooltips.percentageToBusiness')}
                                    </div>
                                  </div>
                                )}

                                {fromAnyToBool(editState?.[key]?.updating) && (
                                  <div className={CN.spinnerOverlay}>
                                    <InlineSpinner variant="small" />
                                  </div>
                                )}

                                {fromAnyToBool(editState?.[key]?.updating) && (
                                  <div className={CN.iconIndicator}>
                                    <Icon variant="confirm" width="md" />
                                  </div>
                                )}
                                {fromAnyToBool(editState?.[key]?.exceedsMax) && (
                                  <div>
                                    <div className={CN.iconIndicator}>
                                      <Icon variant="errorTriangle" width="md" />
                                      <div className={CN.exceedsMaxTooltip}>{t('humanResources.splitTakings.commissionTable.tooltips.maxPercentageReached')}</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Permitted>
    </div>
  );
};

const TimeOffCommissionsPageHoc = connect(
  null,
  mapActionsToDispatchToProps({
    ibeStylistsCommissionsRead,
    ibeStylistsCommissionsCreate,
    ibeStylistsCommissionsUpdate,
  }),
)(StaffCommissions);

export default TimeOffCommissionsPageHoc;
